/**
 * Home — browse by valuer (Phase 3 navigation model). A tile per valuer with their report
 * count + awaiting/done breakdown, the longest-waiting report across everyone, and a
 * "needs attention" filter so the reviewer can still triage quickly even though the
 * primary structure is by valuer.
 */
import { useMemo, useState } from 'react';
import type { BankRow, CheckResultRow, ErrorFindingRow, ReportRow, RiskResultRow, ValuerRow } from '../../reviewer/types';
import {
  deadlineBucket,
  longestWaiting,
  mostUrgentByDeadline,
  needsAttention,
  summariseReport,
  summariseValuers,
  waitingSince,
} from '../../reviewer/aggregate';
import { formatDate } from '../../lib/status';
import { Tile, Button, Chip, activatable } from '../../components/ui';
import { StatusChip } from '../../components/reviewer/StatusChip';
import { WaitingTimer } from '../../components/reviewer/WaitingTimer';

interface ReviewerHomeProps {
  valuers: ValuerRow[];
  banks: BankRow[];
  reports: ReportRow[];
  checkResults: CheckResultRow[];
  riskResults: RiskResultRow[];
  errorFindings: ErrorFindingRow[];
  onOpenValuer: (valuerId: string) => void;
  onOpenReport: (reportId: string) => void;
}

export function ReviewerHome({
  valuers,
  reports,
  checkResults,
  riskResults,
  errorFindings,
  onOpenValuer,
  onOpenReport,
}: ReviewerHomeProps) {
  const [attentionOnly, setAttentionOnly] = useState(false);

  const valuerSummaries = useMemo(() => summariseValuers(valuers, reports), [valuers, reports]);
  const longest = useMemo(() => longestWaiting(reports), [reports]);
  const mostUrgent = useMemo(() => mostUrgentByDeadline(reports), [reports]);
  const mostUrgentBucket = mostUrgent ? deadlineBucket(mostUrgent.neededByAt) : null;

  const attentionReports = useMemo(() => {
    return reports
      .map((r) => ({ report: r, summary: summariseReport(r.id, checkResults, riskResults, errorFindings) }))
      .filter(({ report, summary }) => needsAttention(report.status, summary))
      .sort((a, b) => waitingSince(a.report) - waitingSince(b.report));
  }, [reports, checkResults, riskResults, errorFindings]);

  return (
    <main className="main main--wide">
      <div className="page-head">
        <h1 className="page-title">Reports by valuer</h1>
        <Button variant={attentionOnly ? 'primary' : 'secondary'} onClick={() => setAttentionOnly((v) => !v)}>
          {attentionOnly ? 'Showing needs attention' : 'Needs attention'}
        </Button>
      </div>

      <div className="home-highlights">
        {longest ? (
          <Tile className="tile--pad-lg longest-waiting" {...activatable(() => onOpenReport(longest.id))}>
            <div className="longest-waiting__label">Longest waiting</div>
            <div className="longest-waiting__addr">{longest.addressRaw || 'Address not recorded'}</div>
            <div className="longest-waiting__meta">
              <StatusChip status={longest.status} />
              <span className="muted">waiting</span>
              <WaitingTimer since={longest.submittedAt ?? longest.firstSeenAt} />
            </div>
          </Tile>
        ) : null}

        {mostUrgent ? (
          <Tile className="tile--pad-lg longest-waiting" {...activatable(() => onOpenReport(mostUrgent.id))}>
            <div className="longest-waiting__label">Most urgent — needed by</div>
            <div className="longest-waiting__addr">{mostUrgent.addressRaw || 'Address not recorded'}</div>
            <div className="longest-waiting__meta">
              <StatusChip status={mostUrgent.status} />
              <span
                className={
                  mostUrgentBucket === 'overdue' ? 'mark mark--fail pill' : mostUrgentBucket === 'due_today' ? 'mark mark--flag pill' : 'muted'
                }
              >
                {mostUrgentBucket === 'overdue' ? 'Overdue — ' : mostUrgentBucket === 'due_today' ? 'Due today — ' : 'Needed by '}
                {formatDate(mostUrgent.neededByAt)}
              </span>
            </div>
          </Tile>
        ) : null}
      </div>

      {attentionOnly ? (
        <div className="stack" style={{ marginTop: 'var(--space-6)' }}>
          <h2 className="section__title">Needs attention</h2>
          {attentionReports.length === 0 ? (
            <Tile>
              <p className="muted">Nothing needs attention right now — every open report reads clean.</p>
            </Tile>
          ) : (
            <Tile>
              <div className="uploads">
                {attentionReports.map(({ report, summary }) => (
                  <div
                    className="upload-row upload-row--clickable"
                    key={report.id}
                    {...activatable(() => onOpenReport(report.id))}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="upload-row__addr">{report.addressRaw || 'Address not recorded'}</div>
                      <div className="upload-row__date">
                        {summary.failCount} fail · {summary.flagCount} flag · {summary.riskFlagCount} risk ·{' '}
                        {summary.errorCount} error{summary.errorCount === 1 ? '' : 's'}
                      </div>
                    </div>
                    <StatusChip status={report.status} />
                  </div>
                ))}
              </div>
            </Tile>
          )}
        </div>
      ) : (
        <div className="valuer-grid">
          {valuerSummaries.map((v) => (
            <Tile key={v.valuerId} className="valuer-tile" {...activatable(() => onOpenValuer(v.valuerId))}>
              <div className="valuer-tile__name">{v.displayName}</div>
              <div className="valuer-tile__count">{v.total}</div>
              <div className="valuer-tile__sub">report{v.total === 1 ? '' : 's'}</div>
              <div className="valuer-tile__breakdown">
                <Chip>{v.awaiting} awaiting</Chip>
                <Chip>{v.done} done</Chip>
              </div>
            </Tile>
          ))}
          {valuerSummaries.length === 0 ? (
            <Tile className="tile--pad-lg">
              <div className="empty">
                <div className="empty__title">No valuers yet</div>
                <p className="empty__body">Once a valuer uploads a report, they’ll show up here.</p>
              </div>
            </Tile>
          ) : null}
        </div>
      )}
    </main>
  );
}
